import { ApiError } from "@repo/api-client";
import type { Me } from "@repo/shared-types";
import { queryClient, read } from "./query";
import { __setMeFetcher, queries } from "./queries";

/**
 * The current session, shared across loaders: the `["me"]` query
 * (`lib/queries.ts`), so the layout and a page asking at once make one
 * request, and a page drawn from the cache asks again behind it (0075).
 *
 * The cache is only ever an optimisation. The api authenticates every request
 * on its own, so a stale copy means at worst a page rendering as signed-in
 * until a read answers 401 — which empties the cache — never access to
 * anything.
 */

/** Injection seam for tests; the app never passes this. */
export function __setSessionFetcher(fetcher: (() => Promise<Me>) | null) {
  __setMeFetcher(fetcher);
  queryClient.removeQueries({ queryKey: queries.me().queryKey, exact: true });
}

export function loadSession(): Promise<Me | null> {
  return read(queries.me());
}

/**
 * Reads the session again, now if something shows it, otherwise on the next
 * loader. Called after anything that changes what the session carries — the
 * inbox's count, a picture — that the write itself does not already mark.
 */
export function invalidateSession() {
  void queryClient.invalidateQueries({ queryKey: queries.me().queryKey, exact: true });
}

/**
 * Where to send someone after signing in.
 *
 * Only ever a path inside this app. `//evil.example.com` is the case that
 * catches people out: it starts with a slash, and a browser still reads it as
 * another origin.
 */
export function safeNext(candidate: unknown, fallback = "/"): string {
  if (typeof candidate !== "string" || candidate === "") return fallback;
  if (!candidate.startsWith("/") || candidate.startsWith("//")) return fallback;
  return candidate;
}

/** The field of the request an api error was about, if it named one. */
export function errorField(error: unknown): string | undefined {
  return error instanceof ApiError ? error.field : undefined;
}

/**
 * What an action returns when a call failed: the message, and the field it
 * is about — so the page shows it beside that box, or above the form when it
 * names none.
 */
export type FormError = { error: string; field?: string };

export function formError(error: unknown, fallback: string): FormError {
  const field = errorField(error);
  return field ? { error: errorMessage(error, fallback), field } : { error: errorMessage(error, fallback) };
}

/** The message to show a person when a call failed. */
export function errorMessage(error: unknown, fallback = "Something went wrong"): string {
  if (error instanceof ApiError) return error.message || fallback;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}
